import { useQuery } from '@tanstack/react-query';
import { TFunction } from 'i18next/typescript/t';
import { useMemo } from 'react';

import { TiledModel } from '../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../utils/variables';
import { ProjectGeodataManager } from '../../viewer/project-geodata-manager';
import { useProjectSnapshot } from './use-project-snapshot';

export function useProjectTiledModels() {
  const { projectId: rawProjectId } = useProjectSnapshot();
  const projectId = useMemo(() => rawProjectId ?? undefined, [rawProjectId]);

  const { data } = useQuery({
    queryKey: ['getProjectTiledModels', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      if (!isDefined(projectId)) {
        return [];
      }
      const response = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV1()
        .getTiledModels({
          path: { projectId },
        });

      return response.data;
    },
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
  });

  return data;
}

export function getTimelineTiledModelInfo(tiledModel: TiledModel, t: TFunction) {
  const timelineContainer = ProjectGeodataManager.instance()
    .getTimelineContainers()
    .find((x) => x.tiledModelId === tiledModel.id);

  if (!timelineContainer) {
    return;
  }

  const activeTimeline = ProjectGeodataManager.instance().getActiveTimelineContainer();
  let displayName = `${t('terrainModel.timeline', { ns: 'skyviewAssetMenu' })} ${timelineContainer.containerIndex.toString()} (${tiledModel.name} ${
    timelineContainer.displayDate
  })`;

  if (activeTimeline && timelineContainer.id === activeTimeline.id) {
    displayName = displayName + ` (${t('timeline.current', { ns: 'skyview' })})`;
  }

  return {
    id: timelineContainer.containerIndex.toString(),
    index: timelineContainer.containerIndex,
    geodataId: tiledModel.geodataId,
    tiledModelId: tiledModel.id,
    name: displayName,
  };
}
